import { useInteractionHistoricQuery } from "@/query/historicQuery"
import { InteractionHistoricTable } from "@okeys/shared-utils"
import { Badge } from "../ui/badge"
import { Skeleton } from "../ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table"
import { HistoricEmpty } from "./HistoricStates"
import {
  formatDate,
  getActionTypeInfoInteraction,
  getBadgeVariant,
} from "./utils/historicUtils"

type InteractionHistoricTableProps = {
  searchTerm: string
  selectedActionType: string
  onResetFilters: () => void
}

export const InteractionHistoricTableComponent = ({
  searchTerm,
  selectedActionType,
  onResetFilters,
}: InteractionHistoricTableProps) => {
  const {
    interactionHistoric,
    pendingInteractionHistoric,
    errorInteractionHistoric,
  } = useInteractionHistoricQuery()

  if (pendingInteractionHistoric) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Action</TableHead>
            <TableHead>Acteur</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Heure</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...Array(5)].map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-5 w-5 rounded-full" />
                  <Skeleton className="h-5 w-24" />
                </div>
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 w-40" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 w-24" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 w-20" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  }

  if (errorInteractionHistoric) {
    return (
      <div className="flex h-40 items-center justify-center rounded-md border">
        <p className="text-sm text-muted-foreground">
          Une erreur est survenue lors du chargement de l'historique.
        </p>
      </div>
    )
  }

  if (!interactionHistoric || interactionHistoric.length === 0) {
    return (
      <HistoricEmpty
        searchTerm={searchTerm}
        selectedActionType={selectedActionType}
        onResetFilters={onResetFilters}
      />
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Action</TableHead>
          <TableHead>Acteur</TableHead>
          <TableHead>Date</TableHead>
          <TableHead>Heure</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {interactionHistoric.map(
          (item: InteractionHistoricTable, index: number) => {
            const actor_id = item.actorId as string
            const { date, time } = formatDate(item.timestamp)

            const {
              label,
              color,
              icon: Icon,
            } = getActionTypeInfoInteraction(item)

            return (
              <TableRow key={index}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <Badge variant={getBadgeVariant(color)}>{label}</Badge>
                  </div>
                </TableCell>
                <TableCell>
                  <p className="text-sm">{actor_id || "Inconnu"}</p>
                </TableCell>
                <TableCell>
                  <p className="text-sm">{date}</p>
                </TableCell>
                <TableCell>
                  <p className="text-sm text-gray-600">{time}</p>
                </TableCell>
              </TableRow>
            )
          }
        )}
      </TableBody>
    </Table>
  )
}
